import React, { useState, useEffect } from "react";
import { useContacts } from "@/hooks/useContacts";
import { getDisplayName, formatWhatsAppJid } from "@/utils/contactUtils";
import { Search } from "lucide-react";
import clsx from "clsx";
import type { Database } from "@/lib/supabase";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

interface ContactSearchProps {
  onContactSelect?: (contact: AccountContact) => void;
  onSearchChange?: (searchTerm: string) => void;
  placeholder?: string;
  styles?: string;
}

const ContactSearch: React.FC<ContactSearchProps> = ({
  onContactSelect,
  onSearchChange,
  placeholder = "Pesquisar contatos...",
  styles = "w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] h-12",
}) => {
  const { contacts } = useContacts();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredContacts, setFilteredContacts] = useState<AccountContact[]>(
    []
  );
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredContacts([]);
      setShowResults(false);
      return;
    }

    const filtered = contacts.filter((contact) => {
      const displayName = getDisplayName(contact).toLowerCase();
      const phone = formatWhatsAppJid(contact.jid).toLowerCase();
      const search = searchTerm.toLowerCase();

      return (
        displayName.includes(search) ||
        phone.includes(search) ||
        contact.jid.includes(search)
      );
    });

    setFilteredContacts(filtered.slice(0, 10));
    setShowResults(true);
  }, [searchTerm, contacts]);

  const handleContactClick = (contact: AccountContact) => {
    onContactSelect?.(contact);
    setSearchTerm(getDisplayName(contact));
    setShowResults(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    onSearchChange?.(e.target.value);
  };

  const handleInputFocus = () => {
    if (searchTerm.trim() && filteredContacts.length > 0) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="relative">
      <div
        className={clsx(
          "gap-2 rounded-[12px] border border-border bg-card px-3 flex justify-start items-center",
          "shadow-sm transition-all duration-200",
          "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20",
          styles
        )}
      >
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="grow text-[14px] font-normal text-foreground placeholder:text-muted-foreground/70 w-full h-full border-none focus:outline-none bg-transparent"
        />
      </div>

      {showResults && filteredContacts.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-[12px] shadow-md z-50 max-h-[300px] overflow-y-auto">
          {filteredContacts.map((contact) => {
            const displayName = getDisplayName(contact);
            const formattedPhone = formatWhatsAppJid(contact.jid);

            return (
              <div
                key={contact.id}
                onClick={() => handleContactClick(contact)}
                className="flex items-center gap-3 p-3 hover:bg-hover cursor-pointer transition-colors duration-200 border-b border-border last:border-b-0"
              >
                <div className="w-8 h-8 bg-card-muted rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-foreground">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-foreground truncate">
                    {displayName}
                  </p>
                  <p className="text-[12px] text-muted-foreground truncate">
                    {formattedPhone}
                  </p>
                </div>
                {contact.is_group && (
                  <span className="text-xs bg-card-muted text-muted-foreground px-2 py-1 rounded-lg">
                    Grupo
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContactSearch;
